import React, { lazy, Suspense, useEffect, useState } from "react";
import { HStack, Spinner, useToast } from "@chakra-ui/react";
import { firestore, doc, getDoc, updateDoc, collection, addDoc, query, where, getDocs } from "../firebase/firebaseConfig";
import { useAuth } from "../context/AuthProvider";
import { REWARD_TIERS } from "../utils/rewards"; // Import rewards


// Lazy Load Social Sharing Buttons
const FacebookShareButton = lazy(() => import("react-share").then((mod) => ({ default: mod.FacebookShareButton })));
const TwitterShareButton = lazy(() => import("react-share").then((mod) => ({ default: mod.TwitterShareButton })));
const LinkedinShareButton = lazy(() => import("react-share").then((mod) => ({ default: mod.LinkedinShareButton })));
const WhatsappShareButton = lazy(() => import("react-share").then((mod) => ({ default: mod.WhatsappShareButton })));

const FacebookIcon = lazy(() => import("react-share").then((mod) => ({ default: mod.FacebookIcon })));
const XIcon = lazy(() => import("react-share").then((mod) => ({ default: mod.XIcon })));
const LinkedinIcon = lazy(() => import("react-share").then((mod) => ({ default: mod.LinkedinIcon })));
const WhatsappIcon = lazy(() => import("react-share").then((mod) => ({ default: mod.WhatsappIcon })));


const SocialShare = ({ url, title, quote, hashtag }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [hasVerifiedShares, setHasVerifiedShares] = useState(false);

  // ✅ Generate UTM Link with `shareId`
  const generateUTMUrl = async (platform, userId) => {
    try {
      const shareRef = collection(firestore, "user_shares");

      // 🔥 Create new share entry in Firestore
      const docRef = await addDoc(shareRef, {
        userId,
        platform,
        sharedAt: Date.now(),
        clicks: 0,
        verifiedClicks: 0,
        rewardsGranted: [],
      });

      // ✅ Return the shareable UTM URL with the generated `shareId`
      return {
        utmUrl: `${window.location.origin}?utm_source=${platform}&utm_medium=social&utm_campaign=share&utm_user=${userId}&share_id=${docRef.id}`,
        shareId: docRef.id, // ✅ Store shareId for future updates
      };
    } catch (error) {
      console.error("❌ Error generating UTM link:", error);
      return { utmUrl: "", shareId: null };
    }
  };

  // ✅ Handle Share Button Click
  const handleShare = async (platform) => {
    if (!user) {
      toast({
        title: "Login Required",
        description: "Please log in to earn rewards!",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // ✅ Generate UTM link with `shareId`
    const { utmUrl, shareId } = await generateUTMUrl(platform, user.uid);

    if (!utmUrl || !shareId) {
      console.error("❌ Failed to generate share URL.");
      return;
    }

    console.log(`✅ Share recorded: ${platform} with URL: ${utmUrl}`);

    try {
      // ✅ Update the Firestore document with the generated UTM link
      const shareDocRef = doc(firestore, "user_shares", shareId);
      await updateDoc(shareDocRef, { utmUrl });

      console.log(`🔄 UTM URL updated in Firestore for shareId: ${shareId}`);
    } catch (error) {
      console.error("❌ Error updating UTM URL in Firestore:", error);
    }

    // ✅ Open share popup
    window.open(utmUrl, "_blank");
  };


  // ✅ Verify Shared Links & Reward Users
  useEffect(() => {
    const verifySharedLinks = async () => {
      if (!user || hasVerifiedShares) return;

      const sharesQuery = query(collection(firestore, "user_shares"), where("userId", "==", user.uid));
      const querySnapshot = await getDocs(sharesQuery);

      let totalVerifiedClicks = 0;
      let userRef = doc(firestore, "users", user.uid);
      let userSnap = await getDoc(userRef);
      let userData = userSnap.exists() ? userSnap.data() : {};
      let rewardsGiven = userData.rewardsGiven || {}; // Track awarded milestone rewards
      let freeTrades = userData.freeTrades || 0;

      for (const doc of querySnapshot.docs) {
        const shareData = doc.data();
        totalVerifiedClicks += shareData.verifiedClicks;
      }

      // 🚨 Prevent abuse: Only award rewards when verified clicks match milestones
      let updateData = { totalVerifiedClicks };

      // 🎯 Check for Milestone Rewards (Award only once per milestone)
      let rewardMessage = "";

      // ✅ Award Extra Free Trade at 25 verified clicks
      if (totalVerifiedClicks >= 25 && !rewardsGiven["25"]) {
        updateData["rewardsGiven.25"] = true;
        updateData.freeTrades = freeTrades + 1; // Award free trade
        rewardMessage = "You've earned 1 extra free trade for reaching 25 verified shares! 🎉";

        toast({
          title: "🎉 Free Trade Earned!",
          description: rewardMessage,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        await sendRewardNotification(user.uid, "Free Trade Earned!", rewardMessage);
      }

      // 🎯 Check for other milestone rewards
      for (const [milestone, message] of Object.entries(REWARD_TIERS)) {
        if (totalVerifiedClicks >= milestone && !rewardsGiven[milestone]) {
          updateData[`rewardsGiven.${milestone}`] = true;
          rewardMessage = message;
          toast({
            title: "🎖️ Milestone Achieved!",
            description: message,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          await sendRewardNotification(user.uid, "Milestone Achieved!", message);
        }
      }

      // ✅ Update Firestore with new data if changes were made
      if (Object.keys(updateData).length > 0) {
        await updateDoc(userRef, updateData);
      }

      setHasVerifiedShares(true);
    };

    verifySharedLinks();
  }, [user]);

  // ✅ Function to Send Reward Notifications
  const sendRewardNotification = async (userId, title, message) => {
    const userNotificationsRef = collection(firestore, "users", userId, "notifications");
    await addDoc(userNotificationsRef, {
      title,
      message,
      type: "reward",
      alertStatus: "success",
      createdAt: new Date(),
      status: "unread",
    });

    console.log(`✅ Reward notification sent to user ${userId}: ${title}`);
  };

  return (
    <Suspense fallback={<Spinner size="sm" />}>
      <HStack spacing={3} mt={1}>
        <FacebookShareButton
          url={url}
          quote={quote}
          hashtag={hashtag}
          windowWidth={800}
          windowHeight={600}
          resetButtonStyle={false}
          beforeOnClick={() => console.log("Facebook share button clicked!")}
          onShareWindowClose={() => console.log("Facebook share window closed.")}
          onClick={() => handleShare("Facebook")}
        >
          <FacebookIcon size={40} borderRadius={10} iconFillColor="gray" bgStyle={{ fill: "transparent" }} />
        </FacebookShareButton>

        <TwitterShareButton
          url={url}
          title={title}
          hashtags={hashtag.split("#").slice(1)} // Convert hashtag string to array
          related={["PeerX_Official", "PeerXApp"]} // ✅ Suggested Twitter accounts
          windowWidth={750}
          windowHeight={550}
          resetButtonStyle={false}
          onClick={() => handleShare("Twitter")}
        >
          <XIcon size={40} borderRadius={10} iconFillColor="gray" bgStyle={{ fill: "transparent" }} />
        </TwitterShareButton>

        <LinkedinShareButton
          url={url}
          title={title}
          summary={quote} // ✅ Description of the shared page
          source="PeerX Platform" // ✅ Source (e.g., your app name)
          windowWidth={900}
          windowHeight={650}
          resetButtonStyle={false}
          onClick={() => handleShare("LinkedIn")}
        >
          <LinkedinIcon size={50} borderRadius={10} iconFillColor="gray" bgStyle={{ fill: "transparent" }} />
        </LinkedinShareButton>

        <WhatsappShareButton
          url={url}
          title={title}
          separator=" - " // ✅ Custom separator between text and URL
          windowWidth={600}
          windowHeight={800}
          resetButtonStyle={false}
          onClick={() => handleShare("WhatsApp")}
        >
          <WhatsappIcon size={40} borderRadius={10} iconFillColor="gray" bgStyle={{ fill: "transparent" }} />
        </WhatsappShareButton>
      </HStack>
    </Suspense>
  );
};

export default SocialShare;
